import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Copy, ExternalLink, KeyRound } from "lucide-react";

const href = (u) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);
const copy = (txt, what) => navigator.clipboard.writeText(txt).then(() => toast.success(`${what} copied`)).catch(() => toast.error("Could not copy"));

export const PasswordReveal = ({ vendorId, compact }) => {
  const [pw, setPw] = useState(null);
  const fetchPw = async () => {
    try { const r = await api.get(`/vendors/${vendorId}/portal-password`); setPw(r.data.password); return r.data.password; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return null; }
  };
  const doCopy = async () => { const p = pw ?? await fetchPw(); if (p != null) copy(p, "Password"); };
  return (
    <span className="inline-flex items-center gap-1">
      <span className="font-mono text-xs" data-testid={`vendor-pw-value-${vendorId}`}>{pw == null ? "••••••••" : pw || "(empty)"}</span>
      <Button type="button" size="icon" variant="ghost" className={compact ? "h-7 w-7" : "h-8 w-8"} onClick={() => (pw == null ? fetchPw() : setPw(null))} title={pw == null ? "Show password" : "Hide"} data-testid={`vendor-pw-show-${vendorId}`}>
        {pw == null ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
      </Button>
      <Button type="button" size="icon" variant="ghost" className={compact ? "h-7 w-7" : "h-8 w-8"} onClick={doCopy} title="Copy password" data-testid={`vendor-pw-copy-${vendorId}`}>
        <Copy className="h-3.5 w-3.5" />
      </Button>
    </span>
  );
};

export const VendorPortalCell = ({ v, canSecret }) => {
  if (!v.portal_url && !v.portal_username && !v.has_portal_password) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="space-y-0.5 text-xs" data-testid={`vendor-portal-${v.id}`}>
      {v.portal_url && (
        <a href={href(v.portal_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`vendor-portal-link-${v.id}`}>
          <ExternalLink className="h-3 w-3" /> Open portal
        </a>
      )}
      {v.portal_username && (
        <div className="flex items-center gap-1">
          <span className="font-mono">{v.portal_username}</span>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copy(v.portal_username, "Username")} data-testid={`vendor-user-copy-${v.id}`}><Copy className="h-3 w-3" /></Button>
        </div>
      )}
      {v.has_portal_password && canSecret && <PasswordReveal vendorId={v.id} compact />}
    </div>
  );
};

export const VendorPortalFields = ({ form, setF, vendorId, hasPassword, canSecret }) => {
  const [changing, setChanging] = useState(false);
  const editingPw = !vendorId || !hasPassword || changing;
  return (
    <div className="col-span-2 space-y-3 rounded-md border border-border p-3" data-testid="vendor-portal-section">
      <div className="flex items-center gap-2 text-sm font-semibold"><KeyRound className="h-4 w-4" /> Online Portal Access</div>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 space-y-1.5">
          <Label>Portal Website</Label>
          <Input value={form.portal_url} onChange={(e) => setF("portal_url", e.target.value)} placeholder="e.g. portal.vendor.com" data-testid="vendor-portal-url" />
        </div>
        <div className="space-y-1.5">
          <Label>Username</Label>
          <Input value={form.portal_username} onChange={(e) => setF("portal_username", e.target.value)} autoComplete="off" data-testid="vendor-portal-username" />
        </div>
        <div className="space-y-1.5">
          <Label>Password</Label>
          {!canSecret ? (
            <p className="pt-2 text-xs text-muted-foreground" data-testid="vendor-pw-restricted">{hasPassword ? "Saved — visible to Admin, Regional & Accounting" : "Admin, Regional or Accounting can set this"}</p>
          ) : editingPw ? (
            <Input type="password" value={form.portal_password ?? ""} onChange={(e) => setF("portal_password", e.target.value)} autoComplete="new-password"
              placeholder={hasPassword ? "Enter new password" : ""} data-testid="vendor-portal-password" />
          ) : (
            <div className="flex items-center gap-1">
              <PasswordReveal vendorId={vendorId} />
              <Button type="button" size="sm" variant="outline" className="h-8" onClick={() => setChanging(true)} data-testid="vendor-pw-change">Change</Button>
            </div>
          )}
        </div>
        <div className="col-span-2 space-y-1.5">
          <Label>Portal Notes</Label>
          <Textarea value={form.portal_notes} onChange={(e) => setF("portal_notes", e.target.value)} placeholder="e.g. security question, PIN hint, account owner" rows={2} data-testid="vendor-portal-notes" />
        </div>
      </div>
    </div>
  );
};
